import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChannelsSocketService } from '@service/channels-socket.service';
import { MimicsService } from '@service/mimics.service';
import { Mimic } from 'icreate/common/models/mimic';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { ElementSettings, presetColorKeys } from 'icreate/common/models/editor-viewer-element';
import { ElementSettingsService } from '@service/canvas/element-settings.service';
import { MimicActionsService } from '@mimicViewer/src/lib/services/mimic-actions.service';
import { SystemColorService } from '@service/system-color.service';
import { SystemColorMap } from 'icreate/common/models/system-color';
import { SimulatorService } from '@service/simualtor.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelService } from '@service/channel/channel.service';
import { ElementIntegrationService } from '@service/element-integration.service';
import { ElementIntegrationPreviewData } from 'icreate/common/models/element-integration';
import { LibraryComponentHelperService } from '@service/canvas/library-component-helper.service';
import { ElementPositionHandleHelperService } from '@service/canvas/element-position-handle-helper.service';

@Component({
    selector: 'app-editor-viewer',
    templateUrl: './editor-viewer.component.html',
    styleUrls: [],
    standalone: false,
})
export class EditorViewerComponent implements OnInit, OnDestroy {
    isLoading: boolean = true;
    selectedMimic: Mimic;
    maxHeight: string;
    maxWidth: string;
    elementsSettings: ElementSettings[];
    elementIntegrations: ElementIntegrationPreviewData[];
    channelsUpdateObservable: Observable<any>;
    updatedChannelObservable: Observable<any>;
    defaultColors: SystemColorMap = undefined;
    vesselId: string;
    channelList: Channel[];

    private actionSubscription: Subscription;
    private valueSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private mimicService: MimicsService,
        private elementSettingsService: ElementSettingsService,
        private channelSocketService: ChannelsSocketService,
        private mimicActionsService: MimicActionsService,
        private systemColorSerivce: SystemColorService,
        private simulatorService: SimulatorService,
        private channelService: ChannelService,
        private elementIntegrationService: ElementIntegrationService,
        private libraryComponentHelperService: LibraryComponentHelperService,
        private elementPositionHandleHelperService: ElementPositionHandleHelperService
    ) {}

    @HostListener('window:resize')
    handleWindowResize() {
        this.updateDivisionSize();
    }

    async ngOnInit() {
        await this.simulatorService.startSimualtor();
        this.updateDivisionSize();
        this.actionSubscription = this.mimicActionsService.sendActionToFcu$.subscribe(async ({ elementId }) => {
            await new Promise(res => setTimeout(res, 5000));
            this.mimicActionsService.actionSubjectResponse(elementId);
        });
        this.valueSubscription = this.mimicActionsService.sendValueToFcu$.subscribe(async ({ elementId }) => {
            await new Promise(res => setTimeout(res, 5000));
            this.mimicActionsService.valueSubjectResponse(elementId);
        });
        this.route.params.subscribe(async params => {
            const mimicId = params['mimicId'];
            this.vesselId = params['vesselId'];
            const mimic: Mimic = await this.mimicService.getMimic(mimicId);
            if (!mimic) {
                window.close();
                return;
            }
            this.elementsSettings = await this.elementSettingsService.getAllByMimicId(mimicId);
            this.channelsUpdateObservable = this.channelSocketService.onChannelsValueUpdated$();
            this.updatedChannelObservable = this.channelSocketService.onChannelsUpdated$();
            this.elementIntegrations = await this.elementIntegrationService.getByMimicId(mimicId);
            this.selectedMimic = mimic;
            this.isLoading = false;
        });

        const colorList = await this.systemColorSerivce.getAll(this.vesselId);
        const systemColorMap = colorList.reduce((prev, systemColor, index) => {
            return {
                ...prev,
                [systemColor.name]: {
                    color: systemColor.color,
                    systemColorId: systemColor.id,
                },
            };
        }, {});

        this.defaultColors = {
            ...presetColorKeys,
            ...systemColorMap,
        };

        this.channelList = await lastValueFrom(this.channelService.getChannels(this.vesselId));
    }

    ngOnDestroy(): void {
        this.actionSubscription?.unsubscribe();
        this.valueSubscription?.unsubscribe();
    }

    private updateDivisionSize() {
        this.maxHeight = window.innerHeight.toString();
        this.maxWidth = window.innerWidth.toString();
    }

    showMimicViewer(): boolean {
        return Boolean(this.selectedMimic && this.defaultColors && this.channelList);
    }

    createLibraryComponentElement(event: { currentElement; elementIntegration: ElementIntegrationPreviewData }) {
        let { currentElement, elementIntegration } = event;

        const svgStr = elementIntegration?.elementCondition?.component?.svg;
        if (!svgStr) return;

        const wrapped = `<svg xmlns="http://www.w3.org/2000/svg">${svgStr}</svg>`;

        const parsed = new DOMParser().parseFromString(wrapped, 'image/svg+xml');
        const rootSvg = parsed.documentElement as any;

        const importedRoot = document.importNode(rootSvg, true) as SVGSVGElement;

        const content = importedRoot.firstElementChild as SVGGraphicsElement | null;
        if (!content) return;

        const selectedElementBBox = this.libraryComponentHelperService.getBBoxPosition(currentElement);
        const selectedElementCx = selectedElementBBox.x + selectedElementBBox.width / 2;
        const selectedElementCy = selectedElementBBox.y + selectedElementBBox.height / 2;

        content.setAttribute('id', currentElement.getAttribute('id'));
        content.setAttribute('component', 'true');

        const layer = document.querySelector('.layer') as SVGGElement | null;
        if (layer) {
            layer.appendChild(content);
            content.style.opacity = '0';
        }

        const contentBBox = this.libraryComponentHelperService.getBBoxPosition(content);

        this.elementPositionHandleHelperService.handlePositionChange(
            'X',
            selectedElementCx - contentBBox.width / 2,
            [content],
            true,
            false
        );
        this.elementPositionHandleHelperService.handlePositionChange(
            'Y',
            selectedElementCy - contentBBox.height / 2,
            [content],
            true,
            false
        );
    }
}
